import { Edit } from '@mui/icons-material';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import { CalendarIcon } from '@mui/x-date-pickers-pro';
import { useState } from 'react';

import { VeranstaltungRessourcenCalendar } from '../../components/general';
import {
  capabilities,
  type RessourcenBlockungen,
  type ThemaFromThemaRouter,
  type Veranstaltung,
  type Zeitraum,
} from '../../dtos';
import { getVeranstaltungStart } from '../../utils';
import { useUpdateRessourcenBlockungenOfVeranstaltung } from './useUpdateRessourcenBlockungenOfVeranstaltung.ts';

type UpdateRessourcenBlockungenProps = {
  readonly thema: Pick<
    ThemaFromThemaRouter,
    | 'akaThemaId'
    | 'planungsinformation'
    | 'isOnline'
    | 'planungsformat'
    | 'module'
    | 'manuellerAusschreibungsprozess'
    | 'veranstaltungstypSapId'
  >;
  readonly veranstaltung: Pick<
    Veranstaltung,
    | 'ablauf'
    | 'akaModulId'
    | 'akaVeranstaltungId'
    | 'akaDurchgangId'
    | 'id'
    | 'onlineTool'
    | 'experteBlockungen'
    | 'experteTermine'
    | 'vivaStatus'
    | 'raumBlockungen'
    | 'standortBlockungen'
    | 'teilleistungen'
    | 'capabilities'
    | 'manuellerAusschreibungsprozess'
  >;
  readonly editType: 'experte' | 'raum' | 'all';
  readonly isIconButton?: boolean;
};

export const UpdateRessourcenBlockungen = (props: UpdateRessourcenBlockungenProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { save: saveRessourcenBlockungen } = useUpdateRessourcenBlockungenOfVeranstaltung(props.veranstaltung);

  const necessaryCapabilities =
    props.editType === 'all'
      ? [capabilities.EDIT_EXPERTE, capabilities.EDIT_LOCATION]
      : props.editType === 'experte'
        ? [capabilities.EDIT_EXPERTE]
        : [capabilities.EDIT_LOCATION];
  const isIconButtonEnabled = necessaryCapabilities.every((cap) => props.veranstaltung.capabilities.includes(cap));

  const onSave = async (ressourcenBlockungen: RessourcenBlockungen, ablauf: Zeitraum[]): Promise<void> => {
    await saveRessourcenBlockungen(ressourcenBlockungen, ablauf);
    setIsOpen(false);
  };

  const getTooltipLabel = () => {
    switch (props.editType) {
      case 'experte':
        return 'Trainer:innen ändern';
      case 'raum':
        return 'Location ändern';
      case 'all':
        return 'Ressourcen ändern';
    }
  };

  const getCollapseFocus = (): 'experte' | 'raum' | undefined => {
    if (props.editType === 'all') {
      return undefined;
    }
    return props.editType;
  };

  // If only the location can be edited, but there is no location to edit, this component has no point
  if (props.editType === 'raum' && props.veranstaltung.onlineTool) {
    return null;
  }

  return (
    <Stack>
      {props.isIconButton ? (
        <Tooltip
          title={
            isIconButtonEnabled
              ? getTooltipLabel()
              : 'Das Editieren der Ressourcen ist aktuell nicht möglich. Sobald die Veranstaltung nach SAP übertragen wurde, kannst du die Ressourcen in SAP ändern.'
          }
        >
          <span>
            <IconButton
              onClick={() => setIsOpen(true)}
              disabled={!isIconButtonEnabled}
              color="primary"
              sx={{ ml: 0.5 }}
            >
              <Edit />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button onClick={() => setIsOpen(true)} size="small" startIcon={<CalendarIcon />} sx={{ borderRadius: 1 }}>
          Ressourcen ändern
        </Button>
      )}
      {isOpen && (
        <VeranstaltungRessourcenCalendar
          thema={props.thema}
          veranstaltung={props.veranstaltung}
          onSave={onSave}
          onCancel={() => setIsOpen(false)}
          isFullscreen
          initDate={getVeranstaltungStart(props.veranstaltung)}
          collapseFocus={getCollapseFocus()}
        />
      )}
    </Stack>
  );
};
